
/**
 * **フェイズの切り替え**（Issue #530）。
 *
 * 開く: `new_phase`。画面の中央を光の帯が横に走り、その上にフェイズ名が出て消える。
 *
 * **語彙の検算を兼ねている 1 本**:
 * - `at: { screen }` … 盤ではなく舞台のピクセル（遠近が付かない）
 * - `parent` … 文字を帯にぶら下げて**まとまりごと**動かす
 * - `props` … 文字の中身を payload から渡す（素材は「文字を描く」ことしか知らない）
 */
const COLOR = '#ffd76a';

/** 状況ごとのファイル（`../effects/new_phase_<variant>_effect.js`）が共有する組み立て（Issue #628。arcade はどの状況も同じ絵） */
export const spec = {
  label: 'フェイズ',
  /** フェイズが変わったら舞台の設定を素に戻す（バトルの舞台の閉じ方。Issue #535） */
  scene: 'reset',
  color: COLOR,
  sounds: () => [{ at: 0, name: 'phase_change' }],

  /**
   * フェイズ名が分からないイベントでは出さない。
   *
   * **同じフェイズ名が続くときも出さない** —— core は `battle_step` / `damage` / `damage_calc` を
   * 別のイベントで出すが、見せる文字はどれも BATTLE PHASE なので、そのままだと同じ帯が並ぶ
   * （サンプルの 1 試合で `new_phase` は 254 回。旧 `components/fx/effects/phase.js` も同じ抑制をしている）。
   *
   * **窓は `turn`** —— ターンが変わったら同じ名前でも出す（`phase.js` が `new_turn` で忘れるのと同じ）。
   */
  opens({ payload, history }) {
    if (!payload?.phaseLabel) return null;
    const prev = history.last('new_phase', { within: 'turn' });
    return prev?.payload?.phaseLabel === payload.phaseLabel ? null : payload;
  },

  layers: [
    /*
     * **光の帯**。横に開いて閉じる。素材は止まった 1 枚なので、伸び縮みは器の `scale`。
     * `{ screen: [x, y] }` は**点**なので、大きさは素材が名乗ったもの（1920×300）を使う
     * —— 名乗っていないと層ごと飛ばされて「宣言したのに何も出ない」になる（Issue #531）。
     */
    {
      id: 'band',
      label: '光の帯',
      part: 'beam',
      at: { screen: [960, 540] },
      props: { color: COLOR },
      tf: {
        scale: { keys: [{ ms: 0, value: 0.2 }, { ms: 180, value: 1, ease: 'ease_out' }, { ms: 900, value: 1 }, { ms: 1100, value: 0.2, ease: 'ease_in' }] },
        opacity: { keys: [{ ms: 0, value: 0 }, { ms: 140, value: 1 }, { ms: 900, value: 1 }, { ms: 1100, value: 0 }] },
      },
    },
    /*
     * **フェイズ名**。帯にぶら下げるので、帯が開き切ってから出て、帯と一緒に消える。
     * 親が出ていなければ子も出ない（器が面倒を見る）。
     */
    {
      id: 'label',
      label: 'フェイズ名',
      part: 'text',
      parent: 'band',
      props: { text: '{phaseLabel}', size: 96, color: '#ffffff' },
      tf: {
        opacity: { keys: [{ ms: 200, value: 0 }, { ms: 320, value: 1 }, { ms: 880, value: 1 }, { ms: 1000, value: 0 }] },
        y: { keys: [{ ms: 200, value: 16 }, { ms: 360, value: 0, ease: 'ease_out' }] },
      },
    },
  ],
};
